// apps/share-web/src/ui/app-header.ts
// Unified member app-header across all authed web routes + the player.
// Auth-aware via fetchMe: authed → avatar + popover; anon (public-share player)
// → Sign in + Get the app. Renderer-only except Sign out → POST /api/auth/logout.

import { fetchMe as defaultFetchMe, type MeResult, type Me } from '../me.js';
import { loadCachedMe as defaultLoadCachedMe } from '../offline/open-store.js';
import { signOut as defaultSignOut } from '../auth.js';
import { t, getLocale, setLocale } from './i18n.js';
// Тема — свойство человека, а не страницы (B-260915). Полоса приложения стоит
// на КАЖДОМ маршруте, включая плеер, поэтому переключатель живёт здесь: до
// волны E тему выбирали только на странице слов, и вернуть её из плеера, куда
// стамп <html> уезжал вместе с читателем, было нечем.
import { setAppTheme } from '../theme-boot.js';
import { loadReaderPrefs, saveReaderPrefs, type ReaderTheme } from '../chart/reader-prefs.js';
import { BRAND_LOGO } from '../assets/brand-logo.js';
import { buildEraseOfflineButton } from './erase-offline-button.js';
// Design-system button for the anon CTAs (Sign in / Get the app). These only
// render in the anon state, which only occurs on the player route, where the DS
// base + bw-button CSS are already loaded (player-view/mount.ts, statically
// imported by main.ts → present on every route's bundle). Imported here too so
// app-header is self-contained.
import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-button.css';

// Download → marketing/landing site (single source for "get the desktop app").
const DEFAULT_DOWNLOAD = 'https://bandway.app';
const DEFAULT_FEEDBACK = 'https://github.com/neodisa/band-way/issues/new';

export interface AppHeaderDeps {
  apiBase: string;
  navigate: (path: string) => void;
  fetchMe?: (apiBase: string) => Promise<MeResult>;
  /**
   * Дефект 2 живой приёмки волны G. Владелец продукта, дословно: «появилась
   * кнопка Connection Lost, Offline. И сверху появилось типа «войдите» —
   * хотя должен был остаться аккаунт». `fetchMe` при сетевом отказе отдаёт
   * `{ok:false}` БЕЗ `unauthenticated` — ту же форму, что и волна G уже
   * чинила для библиотеки (главный урок волны: сетевой отказ ≠ отрицательный
   * ответ). Раньше `.then`/`.catch` схлопывали оба исхода в `renderAnon()`.
   *
   * Параметром, а не импортом offline-store напрямую — модуль остаётся
   * тестируемым без хранилища, и орган (кэш) гаснет вместе со своей
   * проводкой (main.ts), не утаскивая этот файл в зависимость от IndexedDB.
   * Не задано — прод получает `loadCachedMe` из offline/open-store.js.
   */
  loadCachedMe?: () => Promise<{ email: string | null; teams: Me['teams'] } | null>;
  signOut?: (apiBase: string) => Promise<{ ok: boolean }>;
  feedbackUrl?: string;
  downloadUrl?: string;
  onToggleRail?: () => void;
  /** When set, the "Report a problem" link opens this instead of feedbackUrl
   *  (web #1 — in-app feedback modal → PostHog $feedback). */
  onReport?: () => void;
  /**
   * Волна G: стереть офлайн-кэш этого человека. Задано — в меню аккаунта
   * появляется кнопка, и выход из аккаунта зовёт то же стирание сам.
   *
   * Это ЕДИНСТВЕННЫЕ два пути стирания во всём продукте: после ревью
   * качества 401 кэш не трогает (один отказ прокси посреди служения не имеет
   * права обнулить библиотеку). Не задано — стирать нечем: страница без
   * хранилища (приватный режим) или вошедший ещё неизвестен.
   */
  eraseOffline?: () => Promise<void>;
}

export interface AppHeaderHandle {
  unmount: () => void;
  /** Resolves after fetchMe settles and the account slot has rendered. */
  ready: Promise<void>;
}

function initials(email: string | null): string {
  const local = (email ?? '?').split('@')[0] ?? '?';
  return (local.slice(0, 2).toUpperCase() || '?');
}

export function mountAppHeader(host: Element, deps: AppHeaderDeps): AppHeaderHandle {
  const fetchMe = deps.fetchMe ?? defaultFetchMe;
  const signOut = deps.signOut ?? defaultSignOut;
  const feedbackUrl = deps.feedbackUrl ?? DEFAULT_FEEDBACK;
  const downloadUrl = deps.downloadUrl ?? DEFAULT_DOWNLOAD;

  host.textContent = '';
  const bar = document.createElement('div');
  bar.className = 'app-header';

  if (deps.onToggleRail) {
    const burger = document.createElement('button');
    burger.type = 'button';
    burger.className = 'ah-burger';
    burger.setAttribute('aria-label', t('topbar.openNav'));
    burger.textContent = '☰';
    burger.addEventListener('click', () => deps.onToggleRail!());
    bar.appendChild(burger);
  }

  const brand = document.createElement('button');
  brand.type = 'button';
  brand.className = 'ah-brand';
  brand.innerHTML = `<span class="ah-logo">${BRAND_LOGO}</span><span class="ah-brand-name">Band Way</span>`;
  brand.addEventListener('click', () => deps.navigate('/library'));
  bar.appendChild(brand);

  const spacer = document.createElement('div');
  spacer.className = 'ah-spacer';
  bar.appendChild(spacer);

  // Language switcher moved out of the top bar to declutter it (Сергей
  // 2026-07-02). Authed users get it as a section in the avatar menu; anon
  // users (no menu) get a compact <select> in the account slot.
  const LANGS: Array<['en' | 'ru' | 'uk', string]> = [['en', 'English'], ['ru', 'Русский'], ['uk', 'Українська']];
  const applyLocale = (loc: 'en' | 'ru' | 'uk'): void => {
    setLocale(loc);
    if (typeof window !== 'undefined') window.location.reload();
  };
  const buildLangSelect = (): HTMLSelectElement => {
    const sel = document.createElement('select');
    sel.className = 'ah-lang';
    sel.setAttribute('aria-label', t('topbar.language'));
    for (const [value, label] of LANGS) {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      sel.appendChild(opt);
    }
    sel.value = getLocale();
    sel.addEventListener('change', () => applyLocale(sel.value as 'en' | 'ru' | 'uk'));
    return sel;
  };

  const report = document.createElement('a');
  report.className = 'ah-report';
  report.textContent = t('topbar.report');
  if (deps.onReport) {
    report.href = '#';
    report.addEventListener('click', (e) => { e.preventDefault(); deps.onReport!(); });
  } else {
    // Fallback (no modal wired): the old external feedback link.
    report.href = feedbackUrl;
    report.target = '_blank';
    report.rel = 'noopener noreferrer';
  }
  bar.appendChild(report);

  const slot = document.createElement('div');
  slot.className = 'ah-account-slot';
  slot.innerHTML = '<span class="ah-skeleton" aria-hidden="true"></span>';
  bar.appendChild(slot);

  host.appendChild(bar);

  // document-level listeners for the open popover (registered on open)
  let onDocClick: ((e: MouseEvent) => void) | null = null;
  let onKey: ((e: KeyboardEvent) => void) | null = null;
  const teardownDocListeners = (): void => {
    if (onDocClick) document.removeEventListener('click', onDocClick);
    if (onKey) document.removeEventListener('keydown', onKey);
    onDocClick = null;
    onKey = null;
  };

  const renderAnon = (): void => {
    slot.innerHTML = '';
    // DS buttons (bw-button): Sign in = neutral cap, Get the app = amber (brand
    // gold) accent. They emit `bw-click`; navigation is wired to that.
    const signin = document.createElement('bw-button');
    signin.setAttribute('data-action', 'signin');
    signin.setAttribute('shape', 'pill');
    signin.setAttribute('size', 'sm');
    signin.textContent = t('topbar.signIn');
    signin.addEventListener('bw-click', () => deps.navigate('/login'));

    const getapp = document.createElement('bw-button');
    getapp.setAttribute('data-action', 'getapp');
    getapp.setAttribute('shape', 'pill');
    getapp.setAttribute('size', 'sm');
    getapp.setAttribute('tone', 'amber');
    getapp.textContent = t('topbar.getApp');
    getapp.addEventListener('bw-click', () => {
      if (typeof window !== 'undefined') window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    });

    // Anon has no avatar menu, so keep the language switcher here (compact).
    slot.appendChild(buildLangSelect());
    slot.appendChild(signin);
    slot.appendChild(getapp);
  };

  const renderAuthed = (me: Me): void => {
    slot.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'ah-account';

    const avatar = document.createElement('button');
    avatar.type = 'button';
    avatar.className = 'ah-avatar';
    avatar.setAttribute('aria-haspopup', 'menu');
    avatar.setAttribute('aria-expanded', 'false');
    avatar.textContent = initials(me.email);

    const menu = document.createElement('div');
    menu.className = 'ah-menu';
    menu.setAttribute('role', 'menu');
    menu.hidden = true;

    const teamName = me.teams && me.teams[0] ? me.teams[0].name : '';
    const head = document.createElement('div');
    head.className = 'ah-menu-head';
    const label = document.createElement('div');
    label.className = 'ah-menu-label';
    label.textContent = t('topbar.signedIn');
    const emailEl = document.createElement('div');
    emailEl.className = 'ah-menu-email';
    emailEl.textContent = me.email ?? '';
    head.appendChild(label);
    head.appendChild(emailEl);
    if (teamName) {
      const teamEl = document.createElement('div');
      teamEl.className = 'ah-menu-team';
      teamEl.textContent = teamName;
      head.appendChild(teamEl);
    }
    menu.appendChild(head);

    const mkItem = (text: string, sub: string | null, extraClass: string, onClick: () => void): HTMLButtonElement => {
      const it = document.createElement('button');
      it.type = 'button';
      it.className = 'ah-menu-item' + (extraClass ? ' ' + extraClass : '');
      it.setAttribute('role', 'menuitem');
      const lbl = document.createElement('span');
      lbl.className = 'ah-menu-item-label';
      lbl.textContent = text;
      it.appendChild(lbl);
      if (sub) {
        const subEl = document.createElement('span');
        subEl.className = 'ah-menu-item-sub';
        subEl.textContent = sub;
        it.appendChild(subEl);
      }
      it.addEventListener('click', onClick);
      return it;
    };

    menu.appendChild(mkItem(t('topbar.myLibrary'), t('topbar.myLibrarySub'), '', () => deps.navigate('/library')));
    menu.appendChild(mkItem(t('topbar.myTeam'), t('topbar.myTeamSub'), '', () => deps.navigate('/dashboard')));
    // Admin entry point (Task 12 of the admin revamp): the SPA no longer hosts
    // the admin shell inline, so this is the only in-app way to reach the
    // standalone /admin page (the other being a bookmark). Admin-only.
    if (me.isAdmin) {
      menu.appendChild(mkItem(t('topbar.admin'), null, '', () => deps.navigate('/admin')));
    }

    // Language section — moved here from the top bar. Radio-style rows with the
    // active language checked.
    const langDivider = document.createElement('div');
    langDivider.className = 'ah-menu-divider';
    menu.appendChild(langDivider);
    const langLabel = document.createElement('div');
    langLabel.className = 'ah-menu-label ah-menu-section-label';
    langLabel.textContent = t('topbar.language');
    menu.appendChild(langLabel);
    const curLoc = getLocale();
    for (const [loc, label] of LANGS) {
      const active = loc === curLoc;
      const item = mkItem(label, null, 'ah-menu-lang' + (active ? ' is-active' : ''), () => applyLocale(loc));
      item.setAttribute('role', 'menuitemradio');
      item.setAttribute('aria-checked', String(active));
      menu.appendChild(item);
    }

    // Секция темы — по устройству близнец секции языка выше, и отличается
    // ровно одним: выбор применяется НА МЕСТЕ, без перезагрузки. Язык
    // перезагружает страницу, потому что подписи уже нарисованы; тема же
    // меняет только набор токенов на <html>, и увести читателя со страницы
    // ради этого значило бы потерять его место в песне.
    //
    // Хранилище: loadReaderPrefs/saveReaderPrefs по контракту НЕ бросают —
    // отказ приватного режима они глотают внутри (reader-prefs.ts), в отличие
    // от сырых обращений к localStorage у соседей. Поэтому try/catch здесь
    // был бы мёртвым кодом.
    const THEMES: Array<[ReaderTheme, string]> = [
      ['dark', t('chart.themeDark')],
      ['light', t('chart.themeLight')],
      ['auto', t('chart.themeAuto')],
    ];
    const themeDivider = document.createElement('div');
    themeDivider.className = 'ah-menu-divider';
    menu.appendChild(themeDivider);
    const themeLabel = document.createElement('div');
    themeLabel.className = 'ah-menu-label ah-menu-section-label';
    themeLabel.textContent = t('topbar.theme');
    menu.appendChild(themeLabel);
    const themeRows: Array<[ReaderTheme, HTMLButtonElement]> = [];
    /** Перерисовать отметки: одна галочка на секцию, и она на выбранном. */
    const paintThemeMarks = (current: ReaderTheme): void => {
      for (const [value, row] of themeRows) {
        const on = value === current;
        row.classList.toggle('is-active', on);
        row.setAttribute('aria-checked', String(on));
      }
    };
    for (const [value, label] of THEMES) {
      const row = mkItem(label, null, 'ah-menu-theme', () => {
        // Владелец стампа ОДИН (theme-boot.ts): иначе слушатель системной темы,
        // повешенный на старте в режиме «Auto», переживал бы явный выбор и
        // перекрашивал экран против него — сценарий B-260915 в чистом виде.
        setAppTheme(value);
        saveReaderPrefs({ theme: value });
        paintThemeMarks(value);
      });
      row.setAttribute('role', 'menuitemradio');
      themeRows.push([value, row]);
      menu.appendChild(row);
    }
    paintThemeMarks(loadReaderPrefs().theme);

    // Кнопка стирания — НАД разделителем и выходом: она про данные на этом
    // телефоне, а не про сессию, и стоять рядом с «выйти» ей нужно только
    // потому, что оба убирают следы человека с чужого устройства.
    if (deps.eraseOffline) {
      menu.appendChild(buildEraseOfflineButton({ erase: deps.eraseOffline }));
    }

    const divider = document.createElement('div');
    divider.className = 'ah-menu-divider';
    menu.appendChild(divider);

    const err = document.createElement('div');
    err.className = 'ah-menu-error';
    err.hidden = true;

    const signoutBtn = mkItem(t('topbar.signOut'), null, 'ah-menu-danger', async () => {
      signoutBtn.disabled = true;
      // СТИРАНИЕ ДО ВЫХОДА, и порядок здесь несущий: после signOut сервер
      // отвечает 401, и если стирание упадёт, повторить его нечем — войти,
      // чтобы попробовать снова, уже нельзя. Отказ стирания не запирает
      // человека в аккаунте: выход — его право, а офлайн — усиление.
      if (deps.eraseOffline) await deps.eraseOffline().catch(() => {});
      const r = await signOut(deps.apiBase);
      if (r.ok) { deps.navigate('/login'); return; }
      signoutBtn.disabled = false;
      err.textContent = t('topbar.signOutError');
      err.hidden = false;
    });
    menu.appendChild(signoutBtn);
    menu.appendChild(err);

    const closeMenu = (): void => {
      menu.hidden = true;
      avatar.setAttribute('aria-expanded', 'false');
      teardownDocListeners();
    };
    const openMenu = (): void => {
      menu.hidden = false;
      avatar.setAttribute('aria-expanded', 'true');
      onDocClick = (e: MouseEvent) => { if (!wrap.contains(e.target as Node)) closeMenu(); };
      onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMenu(); };
      document.addEventListener('click', onDocClick);
      document.addEventListener('keydown', onKey);
    };
    avatar.addEventListener('click', () => { if (menu.hidden) openMenu(); else closeMenu(); });

    // Menu lives INSIDE .ah-account (position:relative) so its CSS
    // position:absolute anchors the popover directly under the avatar.
    // Team name beside the avatar so it's always clear whose team you're in
    // (W9, Сергей 2026-06-22) — not just inside the dropdown.
    if (teamName) {
      const teamPill = document.createElement('span');
      teamPill.className = 'ah-team-pill';
      teamPill.textContent = teamName;
      teamPill.title = teamName;
      wrap.appendChild(teamPill);
    }
    wrap.appendChild(avatar);
    wrap.appendChild(menu);
    slot.appendChild(wrap);
  };

  const loadCachedMe = deps.loadCachedMe ?? defaultLoadCachedMe;

  // Сетевой отказ читается кэшем, а НЕ как «человек не вошёл» — дефект 2.
  // Кэша нет (свежий телефон, приватный режим, ещё не писали) — ни то, ни
  // другое: показывать «Sign in» вошедшему — ложь, показывать аватар без
  // единого известного факта о нём — тоже сомнительно (пустой аватар с
  // инициалами '?' выглядел бы как настоящий аккаунт с недостающими
  // данными). Решение — скрыть блок аккаунта целиком до первого успешного
  // `fetchMe`: `renderNeutral` ничего не утверждает, вместо утверждения
  // неправды в любую сторону.
  const renderNeutral = (): void => { slot.innerHTML = ''; };
  const fromCacheOrNeutral = async (): Promise<void> => {
    const cached = await loadCachedMe().catch(() => null);
    if (cached) { renderAuthed({ userId: '', email: cached.email, isAdmin: false, teams: cached.teams }); return; }
    renderNeutral();
  };

  const ready = fetchMe(deps.apiBase)
    .then((res) => {
      if (res.ok) { renderAuthed(res.me); return; }
      // `unauthenticated` — честный отрицательный ответ сервера: человек
      // действительно вышел (или никогда не входил). Единственная ветка,
      // которая имеет право нарисовать «Sign in».
      if (res.unauthenticated) { renderAnon(); return; }
      // Иначе — сетевой отказ БЕЗ ответа сервера: та же форма, что уже чинили
      // для библиотеки. НЕ `renderAnon()`.
      return fromCacheOrNeutral();
    })
    // Брошенное исключение (сеть недоступна физически) — тот же сетевой
    // отказ, что и `{ok:false}` без `unauthenticated` выше, а не «вышел»:
    // catch едет в ТУ ЖЕ ветку, не в renderAnon.
    .catch(() => fromCacheOrNeutral());

  return {
    ready,
    // Тему на unmount НЕ снимаем: она свойство человека, а не маршрута, и её
    // владелец переживает полосу приложения (theme-boot.ts).
    unmount: () => { teardownDocListeners(); host.textContent = ''; },
  };
}
